import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown } from 'lucide-react';

import type { Product } from '@/Data/products';
import { fetchProducts } from '@/services/ProductService';

export default function ProductList() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<
    'Featured' | 'Newest' | 'Price: Low to High' | 'Price: High to Low'
  >('Featured');

  const allProductsRef = useRef<HTMLDivElement | null>(null);

  const scrollToAllProducts = () =>
    allProductsRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

  // ==================================================
  // LOAD PRODUCTS FROM API
  // ==================================================
  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const data = await fetchProducts();
        setProducts(data);
      } catch (err) {
        setError('Failed to load products.');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  // ==================================================
  // FILTERING + SORTING LOGIC
  // ==================================================
  const featuredProducts = products.filter((p) => p.isFeatured);

  let filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()),
  );

  if (sort === 'Price: Low to High')
    filteredProducts = [...filteredProducts].sort((a, b) => a.price - b.price);

  if (sort === 'Price: High to Low')
    filteredProducts = [...filteredProducts].sort((a, b) => b.price - a.price);

  if (sort === 'Newest')
    filteredProducts = [...filteredProducts].sort((a, b) => b.id - a.id);

  // ==================================================
  // PRODUCT CARD
  // ==================================================
  const ProductCard = ({ p }: { p: Product }) => {
    const image =
      p.thumbnails?.[0] ||
      p.mediaGallery.find((m) => m.fileType === 'image')?.fileUrl ||
      '/fallback/no-image.png';

    return (
      <Link
        to={`/${p.slug}`}
        className="group block cursor-pointer overflow-hidden rounded-lg bg-[#0B0B0B] transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#FFBD59]"
        aria-label={`View details for ${p.name}`}
      >
        <div className="relative w-full overflow-hidden">
          <div className="aspect-[4/3] w-full">
            <img
              src={image}
              alt={p.name}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition duration-500 group-hover:opacity-90"
            />
          </div>
        </div>
        <div className="p-4 text-white">
          <h3 className="text-xs font-bold uppercase sm:text-sm">{p.name}</h3>
          <p className="mt-1 text-sm font-semibold text-[#FFBD59] sm:text-base">
            ₱ {p.price.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
          </p>
        </div>
      </Link>
    );
  };

  // ==================================================
  // RENDER STATES
  // ==================================================
  if (loading)
    return (
      <div className="w-full py-20 text-center text-gray-600">
        Loading products...
      </div>
    );

  if (error)
    return <div className="w-full py-20 text-center text-red-500">{error}</div>;

  return (
    <section
      data-aos="fade-up"
      className="mx-auto max-w-5xl bg-white px-4 py-12 sm:px-6 2xl:max-w-7xl"
    >
      <div className="mx-auto w-full max-w-7xl">
        {/* Featured */}
        <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <h2 className="text-xl font-extrabold text-[#0B0B0B] sm:text-2xl md:text-3xl">
            Featured Products
          </h2>
          <button
            onClick={scrollToAllProducts}
            className="rounded-md px-1.5 py-1 text-sm font-medium text-gray-600 underline-offset-2 transition hover:text-[#FFBD59] hover:underline md:text-xl"
          >
            See all
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 tracking-tighter sm:grid-cols-2 md:grid-cols-3">
          {featuredProducts.map((p) => (
            <ProductCard key={p.slug} p={p} />
          ))}
        </div>

        {/* All Products */}
        <div ref={allProductsRef} className="mt-12 scroll-mt-24 sm:mt-14">
          <div className="mb-4 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
            {/* LEFT: Title */}
            <h2 className="text-xl font-extrabold text-[#0B0B0B] sm:text-2xl md:text-3xl">
              All Products
            </h2>

            {/* RIGHT SIDE: Search + Sort */}
            <div className="flex w-full flex-col gap-3 sm:ml-6 sm:w-auto sm:flex-row sm:items-center">
              {/* Search */}
              <label className="relative w-full sm:w-72 md:w-80">
                <Search
                  size={16}
                  className="absolute left-3 top-2.5 text-gray-500"
                />
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-gray-100 py-2 pl-9 pr-3 text-sm text-gray-700 outline-none focus:border-[#FFBD59] focus:ring-1 focus:ring-[#FFBD59]"
                />
              </label>

              {/* Sort */}
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="whitespace-nowrap text-sm text-gray-700">
                  Sort by:
                </span>

                <div className="relative w-full sm:w-40 md:w-48">
                  <select
                    id="sort"
                    value={sort}
                    onChange={(e) => setSort(e.target.value as typeof sort)}
                    className="w-full appearance-none rounded-md border border-gray-300 bg-gray-100 py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-[#FFBD59] focus:ring-1 focus:ring-[#FFBD59]"
                  >
                    <option>Featured</option>
                    <option>Newest</option>
                    <option>Price: Low to High</option>
                    <option>Price: High to Low</option>
                  </select>

                  <ChevronDown
                    size={16}
                    className="pointer-events-none absolute right-2.5 top-2.5 text-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* PRODUCT GRID */}
          <div className="grid grid-cols-1 gap-4 tracking-tighter sm:grid-cols-2 md:grid-cols-3">
            {filteredProducts.map((p) => (
              <ProductCard key={p.slug} p={p} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
